import { useEffect, useState } from "react";

/**
 * Header + grid/list toggle, matching the MikRouter user pages: an eyebrow,
 * a large title, a one-line description, and a segmented view switch on the
 * right. The chosen view persists in localStorage exactly as it does there.
 */
export function useViewMode(): [string, (v: string) => void] {
  const [view, setView] = useState('grid');

  useEffect(() => {
    const saved = window.localStorage.getItem('viewMode');
    if (saved) setView(saved);
  }, []);

  function change(v: string) {
    setView(v);
    window.localStorage.setItem('viewMode', v);
  }

  return [view, change];
}

export function ListHeader({ eyebrow, title, description, view, setView, actions }: any) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">

      <div className="min-w-0">
        <p className="eyebrow">{eyebrow}</p>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>

      <div className="flex shrink-0 items-center gap-2">

        {actions}

        {setView && (
          <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white sm:flex">
            <button
              type="button"
              onClick={() => setView('grid')}
              aria-label="Grid view"
              className={`px-3 py-2 transition ${view === 'grid' ? 'bg-slate-950 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                <path fillRule="evenodd" d="M4.25 2A2.25 2.25 0 0 0 2 4.25v2.5A2.25 2.25 0 0 0 4.25 9h2.5A2.25 2.25 0 0 0 9 6.75v-2.5A2.25 2.25 0 0 0 6.75 2h-2.5Zm0 9A2.25 2.25 0 0 0 2 13.25v2.5A2.25 2.25 0 0 0 4.25 18h2.5A2.25 2.25 0 0 0 9 15.75v-2.5A2.25 2.25 0 0 0 6.75 11h-2.5Zm9-9A2.25 2.25 0 0 0 11 4.25v2.5A2.25 2.25 0 0 0 13.25 9h2.5A2.25 2.25 0 0 0 18 6.75v-2.5A2.25 2.25 0 0 0 15.75 2h-2.5Zm0 9A2.25 2.25 0 0 0 11 13.25v2.5A2.25 2.25 0 0 0 13.25 18h2.5A2.25 2.25 0 0 0 18 15.75v-2.5A2.25 2.25 0 0 0 15.75 11h-2.5Z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              aria-label="List view"
              className={`px-3 py-2 transition ${view === 'list' ? 'bg-slate-950 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                <path fillRule="evenodd" d="M2 3.75A.75.75 0 0 1 2.75 3h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 3.75Zm0 4.167a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Zm0 4.166a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Zm0 4.167a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

      </div>
    </div>
  )
}

export function ListEmpty({ message, action }: any) {
  return (
    <div className="mt-12 rounded-[2rem] border border-slate-200 bg-white p-12 text-center shadow-sm">
      <p className="text-sm font-semibold text-slate-400">{message}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function ListLoading({ label }: any) {
  return (
    <div className="mt-12 flex flex-col items-center gap-3">
      <svg className="h-5 w-5 animate-spin text-slate-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <p className="text-sm text-slate-400">{label}</p>
    </div>
  )
}

/** Relative-vs-absolute date, toggled by clicking — same affordance as MikRouter. */
export function timeAgo(dateStr: any) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}
